/**
 * Export services offered by the data export block, keyed like the runtime registry
 * (fidsy-runtime/src/blocks/data-export/services).
 *
 * Each service keeps its settings in the block payload under its own `configKey`, so
 * settings for different services never mix or overwrite each other, and switching
 * service keeps the other service's settings for switching back. Each service also
 * brings its own settings panel, defaults and validation, and says which source and
 * destination adapter kinds it works with. Adding a service is one entry here, one
 * settings panel and its runtime module.
 *
 * @module data-export/services
 */
import { DATA_ADAPTERS } from '@/constants';
import ApacheParquetExportConfig from './ApacheParquetExportConfig.vue';
import * as apacheParquet from './apache-parquet';

/**
 * @typedef {object} ExportService
 * @property {string} key Registry key, stored in the block's `service` field.
 * @property {string} name Display name.
 * @property {string} configKey Block payload key holding this service's settings.
 * @property {string[]} sources DATA_ADAPTERS keys the service can read from.
 * @property {string[]} destinations DATA_ADAPTERS keys the service can write to.
 * @property {object} component Settings panel, bound with v-model to the service's settings.
 * @property {function(): object} defaultConfig Settings for a newly chosen service.
 * @property {function(object): boolean} validate Whether the settings are complete.
 */

/** @type {Object<string, ExportService>} */
const SERVICES = {
  APACHE_PARQUET: {
    key: 'APACHE_PARQUET',
    name: 'Apache Parquet',
    configKey: 'parquet_config',
    sources: [DATA_ADAPTERS.POSTGRESQL.key],
    destinations: [DATA_ADAPTERS.AZURE_BLOB.key],
    component: ApacheParquetExportConfig,
    defaultConfig: apacheParquet.defaultConfig,
    validate: apacheParquet.validate,
  },
};

export default SERVICES;
